/**
 * SessionTabsPreview - displays a row of "fake" tabs showing session's open tabs
 *
 * Features:
 * - Shows as many tabs as fit in available space
 * - Overflow tabs are represented by "+X more" element
 * - Hover on "+X more" shows popover with remaining tab names
 *
 * Used by: SessionsDialog, SessionListCard
 */
import { useRef, useState, useEffect } from 'react';
import { FileCode, Table2 } from 'lucide-react';
import type { SerializedTab } from '../types/sessionTypes';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';

// ============================================================================
// Types
// ============================================================================

interface SessionTabsPreviewProps {
  tabs: SerializedTab[];
  className?: string;
}

// ============================================================================
// Helpers
// ============================================================================

function TabIcon({ type, className }: { type: SerializedTab['type']; className?: string }) {
  if (type === 'table-explorer') {
    return <Table2 className={cn('h-3 w-3 shrink-0', className)} />;
  }
  return <FileCode className={cn('h-3 w-3 shrink-0', className)} />;
}

// ============================================================================
// Component
// ============================================================================

export function SessionTabsPreview({ tabs, className }: SessionTabsPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  // Start with 0 to prevent layout expansion, will be set after measurement
  const [visibleCount, setVisibleCount] = useState(0);
  const [measured, setMeasured] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const measureContainer = measureRef.current;
    const moreButton = moreButtonRef.current;
    if (!container || !measureContainer) return;

    const calculateVisible = () => {
      // Use the container's own width as the constraint
      const availableWidth = container.offsetWidth;
      if (availableWidth === 0) return;

      const children = Array.from(measureContainer.children) as HTMLElement[];
      if (children.length === 0) return;

      const gap = 6; // gap-1.5 = 6px
      // Measure actual "more" button width if available, otherwise estimate
      const moreButtonWidth = moreButton?.offsetWidth ?? 55;

      let totalWidth = 0;
      let count = 0;

      for (let i = 0; i < children.length; i++) {
        const childWidth = children[i].offsetWidth;
        const widthWithGap = totalWidth + (count > 0 ? gap : 0) + childWidth;
        const remainingTabs = tabs.length - count - 1;
        const needsMoreButton = remainingTabs > 0;
        const maxWidth = availableWidth - (needsMoreButton ? moreButtonWidth + gap : 0);

        if (widthWithGap <= maxWidth) {
          totalWidth = widthWithGap;
          count++;
        } else {
          break;
        }
      }

      // Ensure at least 1 tab is shown if there are tabs
      const newCount = Math.max(count, tabs.length > 0 ? 1 : 0);
      setVisibleCount(newCount);
      setMeasured(true);
    };

    // Reset measured state when tabs change
    setMeasured(false);
    setVisibleCount(0);

    // Initial calculation after layout is ready
    const timer = requestAnimationFrame(() => {
      calculateVisible();
    });

    // Recalculate on resize
    const resizeObserver = new ResizeObserver(() => {
      calculateVisible();
    });
    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(timer);
      resizeObserver.disconnect();
    };
  }, [tabs]);

  if (tabs.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground italic', className)}>
        No open tabs
      </div>
    );
  }

  const visibleTabs = tabs.slice(0, visibleCount);
  const overflowTabs = tabs.slice(visibleCount);
  const hasOverflow = overflowTabs.length > 0;

  return (
    <div ref={containerRef} className={cn('w-full min-w-0', className)}>
      {/* Hidden container for measuring tab widths - positioned off-screen */}
      <div
        ref={measureRef}
        className="fixed left-[-9999px] top-0 flex items-center gap-1.5 pointer-events-none"
        aria-hidden="true"
      >
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50 border border-border/50 text-xs max-w-[140px] shrink-0"
          >
            <TabIcon type={tab.type} className="text-muted-foreground" />
            <span className="truncate">{tab.title}</span>
          </div>
        ))}
      </div>

      {/* Hidden "more" button for measuring its width */}
      <div
        ref={moreButtonRef}
        className="fixed left-[-9999px] top-0 inline-flex items-center px-2 py-1 rounded-md text-xs pointer-events-none"
        aria-hidden="true"
      >
        +{tabs.length} more
      </div>

      {/* Visible container */}
      <div
        className="flex items-center gap-1.5"
        data-testid="session-tabs-preview"
      >
        {!measured ? (
          // Placeholder while measuring
          <div className="text-xs text-muted-foreground tabular-nums">
            {tabs.length} tab{tabs.length !== 1 ? 's' : ''}
          </div>
        ) : (
          <>
            {visibleTabs.map((tab) => (
              <div
                key={tab.id}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50 border border-border/50 text-xs max-w-[140px] shrink-0"
                title={tab.title}
                data-testid="session-tab-chip"
              >
                <TabIcon type={tab.type} className="text-muted-foreground" />
                <span className="truncate">{tab.title}</span>
              </div>
            ))}

            {/* Overflow indicator with popover */}
            {hasOverflow && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center px-2 py-1 rounded-md bg-muted/30 border border-border/30 text-xs text-muted-foreground tabular-nums hover:bg-muted/50 transition-colors shrink-0"
                    data-testid="session-tabs-overflow"
                  >
                    +{overflowTabs.length} more
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  className="w-auto max-w-[280px] p-2"
                >
                  <div className="space-y-1">
                    {overflowTabs.map((tab) => (
                      <div
                        key={tab.id}
                        className="flex items-center gap-2 px-2 py-1 rounded text-xs hover:bg-muted/50"
                      >
                        <TabIcon type={tab.type} className="text-muted-foreground" />
                        <span className="truncate">{tab.title}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </>
        )}
      </div>
    </div>
  );
}
